import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../../../atom/Dropdown/Dropdown";

import { CheckIcon } from "../../../../icons";
import SpaceDot from "../../../atom/Space/SpaceDot";
import { SpaceType } from "../../../../types/space";
import SpaceAddModal from "../../../../views/TaskDetail/Space/SpaceAddModal";
import { getSpaceColor } from "../../../../services/space/space.service";
import { useRef, useState } from "react";
import clsx from "clsx";
import { ReactComponent as DeleteIcon } from "../../../../assets/TaskDetail/delete-icon.svg";
import { AddIcon } from "../../../../icons";
import { useRecoilValue } from "recoil";
import { inProgressAndCompletedSpaceListState } from "../../../../recoil/spaces/inProgressSpaceListState";

interface SpaceDropdownProps {
  selectedValue?: string;
  spaceList: SpaceType[];
  onSelect: (value: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectDropdownRef: React.RefObject<HTMLDivElement>;
  handleDeleteProject: (event: React.MouseEvent) => void;
  onFocusClose: () => void;
  onKeydownEscape: () => void;
}

const SpaceDropdown = ({
  selectedValue,
  spaceList,
  onSelect,
  open,
  onOpenChange,
  onFocusClose,
  onKeydownEscape,
  projectDropdownRef,
  handleDeleteProject,
}: SpaceDropdownProps) => {
  // 기존에 선택된 space가 archive된 경우 선택된 space 노출은 해주기 위해 추가
  const inProgressAndCompletedSpaceList = useRecoilValue(inProgressAndCompletedSpaceListState);

  const spaceAddButtonRef = useRef<HTMLButtonElement>(null);

  const [isProjectAddModal, setIsProjectAddModal] = useState(false);
  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const handelCloseAddModal = () => {
    setIsProjectAddModal(false);
    spaceAddButtonRef.current?.focus();
  };

  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    // esc 눌렀을 때 전파 막기(해당 dropdown만 닫히도록)
    e.stopPropagation();

    // add modal 열려있을 때 esc 눌렀을 때 모달 닫기
    if (isProjectAddModal) {
      e.preventDefault(); // esc 눌렀을 때 해당 모달만 닫히도록 하기 위해 추가(없으면 전체 닫힘)
      setIsProjectAddModal(false);
      spaceAddButtonRef.current?.focus();
      return;
    }

    onKeydownEscape();
  };

  const handleDropdownItemKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, spaceId: number) => {
    if (e.key === "Enter") {
      e.stopPropagation(); // enter 눌렀을 때 상위 모달 닫히지 않도록 막기
      handleOpenChange(false);
    }
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={handleOpenChange}
      modal={false}
      className="box-border min-w-0 max-w-full"
    >
      <DropdownMenuTrigger
        className={clsx(
          "box-border w-full min-w-0 cursor-pointer overflow-hidden rounded-[5px] hover:bg-w-8-hover",
          open && "bg-pressed"
        )}
      >
        <div className="relative box-border flex w-full min-w-0 overflow-hidden text-sm font-normal">
          {selectedValue ? (
            <div
              className="group relative box-border flex h-[30px] cursor-pointer items-center justify-between gap-1.5 self-stretch overflow-hidden text-ellipsis rounded-[5px] px-2 py-0"
              style={{
                backgroundColor:
                  "" + getSpaceColor(inProgressAndCompletedSpaceList, Number(selectedValue)) + "40",
              }}
            >
              <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                <SpaceDot
                  color={getSpaceColor(inProgressAndCompletedSpaceList, Number(selectedValue))}
                  className="flex-shrink-0"
                />
                <span className="min-w-0 select-none overflow-hidden text-ellipsis whitespace-nowrap text-sm font-normal leading-[150%] text-white">
                  {
                    inProgressAndCompletedSpaceList.filter(
                      (project) => project.id === Number(selectedValue)
                    )[0]?.title
                  }
                </span>
              </div>

              <div
                className="invisible absolute right-[3px] top-1/2 flex flex-shrink-0 -translate-y-1/2 cursor-pointer items-center justify-center gap-2.5 rounded-[5px] bg-[rgba(36,38,38,0.8)] p-1 backdrop-blur-[2px] transition-all duration-200 ease-[cubic-bezier(0,1,0,1)] group-hover:visible"
                onClick={handleDeleteProject}
              >
                <DeleteIcon className="mb-[1px] ml-[1px] fill-[rgba(255,255,255,0.24)] group-hover:fill-white" />
              </div>
            </div>
          ) : (
            <div className="flex h-[30px] w-full items-center rounded-[5px] px-2 font-['Pretendard_Variable'] text-sm font-normal text-placeholder">
              Add a space
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={projectDropdownRef}
        sideOffset={5}
        align={"start"}
        side={"bottom"}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          setIsProjectAddModal(false);
          onFocusClose();
        }}
        onEscapeKeyDown={handleEscapeKeyDown}
        className="w-[650px] rounded-[5px] border border-[#2A2C2C] bg-[#1A1C1C] py-1 pl-2 pr-0"
        maxHeight={224}
        showScrollbar
      >
        <DropdownMenuRadioGroup
          value={selectedValue}
          onValueChange={(value) => {
            onSelect(Number(value));
          }}
        >
          {spaceList.map((space) => (
            <DropdownMenuRadioItem
              key={space.id}
              value={space.id.toString()}
              className={
                "relative flex h-9 w-full cursor-pointer select-none items-center gap-1.5 rounded-[5px] px-2 text-sm leading-4 text-white outline-none data-[highlighted]:bg-w-8-hover data-[state=checked]:text-moba"
              }
              onKeyDown={(e) => handleDropdownItemKeyDown(e, Number(space.id))}
            >
              <SpaceDot
                color={space.color}
                className="flex-shrink-0"
              />
              <div className="flex-grow overflow-hidden text-ellipsis whitespace-nowrap">
                {space.title}
              </div>
              {selectedValue === space.id.toString() && <CheckIcon />}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuItem
          asChild
          className="relative flex h-9 w-full select-none items-center gap-1.5 rounded-[5px] px-2 text-sm leading-4 text-white outline-none data-[highlighted]:bg-w-8-hover"
          onSelect={(e) => {
            e.preventDefault();
            setIsProjectAddModal(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.stopPropagation();
              setIsProjectAddModal(true);
            }
          }}
        >
          <button
            ref={spaceAddButtonRef}
            className="box-border w-full focus:outline-none"
          >
            <div className="flex cursor-pointer items-center gap-[6px] text-base">
              <AddIcon color={"#CACDCE"} />
              <span className="text-sm font-normal text-[#cacdce] opacity-90">
                {spaceList.length ? "New Space" : "Create your first space"}
              </span>
            </div>
          </button>
        </DropdownMenuItem>
        {isProjectAddModal && <SpaceAddModal onClose={handelCloseAddModal} />}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SpaceDropdown;
